import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMarkets } from '@/hooks/useMarkets';
import { SPORTS } from '@/components/layout/sports';
import { useBetSlip } from './betSlipContext';
import { findEvent } from './fixtures';
import { marketHeading } from './lineLabels';
import { EmptyState, Glyph, MarketSkeleton, SelectionButtons } from './MarketCard';
import { IconAlert, IconBack, IconCalendar, formatKickoff } from './marketUi';

/*
  sport → match → EVERY market on the match (stakeholder item 1, 2026-10-13).

  The page is a pure projection of /markets grouped by fixture (fixtures.ts).
  It renders one section per market the feed supplies for this event — head to
  head today; handicap, totals, player markets and the rest the moment the
  adapter maps them, with no change here. The honest state of the feed is said
  once, quietly, at the bottom, rather than implied by an empty-looking page.
*/
export function EventPage() {
  const { fixtureId = '' } = useParams();
  const navigate = useNavigate();
  const { data: markets, isLoading, isError, refetch } = useMarkets();
  const { isPicked, pick } = useBetSlip();

  const event = findEvent(markets, fixtureId);
  const sport = event ? SPORTS.find((s) => s.id === event.sport || s.name.toLowerCase() === event.sport.toLowerCase()) : undefined;
  const backTo = sport ? `/bets?sport=${encodeURIComponent(sport.id)}` : '/bets';
  const backLabel = sport ? `Back to ${sport.name}` : 'Back to markets';

  return (
    <div className="flex flex-col gap-6">
      <button onClick={() => navigate(backTo)} className="inline-flex items-center gap-1.5 self-start text-sm text-white/55 hover:text-white transition">
        <Glyph icon={IconBack} size={16} /> {backLabel}
      </button>

      {isLoading ? (
        <>
          <div className="h-20 rounded-2xl bg-white/[0.04] animate-pulse" />
          <MarketSkeleton />
        </>
      ) : isError ? (
        <EmptyState
          icon={IconAlert}
          title="Couldn't load this event"
          subtitle="Something went wrong reaching the odds feed. Give it another go."
          action={<button onClick={() => refetch()} className="mt-1 rounded-lg border border-white/15 px-3 py-1.5 text-sm font-semibold text-brand-teal hover:bg-white/10 transition">Retry</button>}
        />
      ) : !event ? (
        <EmptyState
          icon={IconCalendar}
          title="This event isn't on the board right now"
          subtitle="It may have started, finished, or been withdrawn. Markets refresh through the day."
          action={<Link to="/bets" className="mt-1 rounded-lg border border-white/15 px-3 py-1.5 text-sm font-semibold text-brand-teal hover:bg-white/10 transition">Browse markets</Link>}
        />
      ) : (
        <>
          {/* Hero */}
          <div className="rounded-2xl border border-white/[0.08] bg-gradient-to-br from-brand-surface to-brand-navy2 p-5">
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-white/45">
              {event.live ? <span className="inline-flex items-center gap-1.5 text-red-400"><span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />Live</span> : formatKickoff(event.startAt)}
              {event.league && <><span className="text-white/20">·</span><span>{event.league}</span></>}
            </div>
            <h1 className="font-display font-extrabold text-2xl sm:text-[28px] tracking-[-0.02em] mt-2 leading-tight">
              {event.eventLabel.split(/\s+vs\.?\s+/i).map((t, i, arr) => (
                <span key={t}>
                  {t}
                  {i < arr.length - 1 && <span className="text-white/30 font-semibold text-lg mx-2">vs</span>}
                </span>
              ))}
            </h1>
            <div className="text-xs text-white/40 mt-2">{event.markets.length} {event.markets.length === 1 ? 'market' : 'markets'}</div>
          </div>

          {/* One section per market */}
          {event.markets.map((m) => (
            <section key={m.id} className="rounded-2xl border border-white/[0.08] bg-brand-surface/80 p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-display font-bold text-[15px]">{marketHeading(m)}</h2>
                <span className="text-[11px] text-white/35">{m.selections.length} {m.selections.length === 1 ? 'option' : 'options'}</span>
              </div>
              <SelectionButtons market={m} active={isPicked} onPick={pick} />
            </section>
          ))}

          <p className="text-xs text-white/30 text-center">
            Markets shown are the ones our feed currently supplies for this event. More are added as the feed expands.
          </p>
        </>
      )}
    </div>
  );
}
